import { useState, useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import {
  isPostHogAvailable,
  safePostHogGetFeatureFlag,
  safePostHogGetFeatureFlagPayload,
  safePostHogOnFeatureFlags,
} from 'utils/posthogHelpers';

/**
 * Safe wrapper for PostHog feature flag hooks with recursion protection.
 *
 * This hook replaces direct usage of useFeatureFlagVariantKey from posthog-js/react
 * to prevent "Maximum call stack size exceeded" errors caused by PostHog's internal
 * recursion issues when feature flags trigger events that trigger more flag checks.
 *
 * @param {string} flagKey - The feature flag key to check
 * @returns {string|boolean|null} The feature flag variant value, or null if unavailable
 */
export const useSafeFeatureFlagVariantKey = (flagKey, { sendEvent = true } = {}) => {
  const [variant, setVariant] = useState(null);
  const mountedRef = useRef(true);
  const exposureFiredRef = useRef(false);

  useEffect(() => {
    if (!flagKey) return undefined;
    mountedRef.current = true;
    exposureFiredRef.current = false;

    if (!isPostHogAvailable()) {
      return () => {
        mountedRef.current = false;
      };
    }

    // Seed the initial value for first render WITHOUT firing an exposure. Flags
    // may not be loaded yet; reading with send_event here is what previously
    // logged a stream of null `$feature_flag_called` events and then locked the
    // variant at that pre-load null (PD audit #8).
    const initial = safePostHogGetFeatureFlag(flagKey, { sendEvent: false });
    if (mountedRef.current && initial !== null && initial !== undefined) {
      setVariant(initial);
    }

    // Resolve the flag — and fire the exposure exactly once — when PostHog's
    // flags are ready. onFeatureFlags fires immediately if flags are already
    // loaded, otherwise when loading completes (and on every reload), so the
    // variant no longer sticks at the pre-load null and the exposure
    // (`$feature_flag_called`) carries the real `control`/`test` value.
    safePostHogOnFeatureFlags(() => {
      if (!mountedRef.current) return;
      const fireExposure = sendEvent && !exposureFiredRef.current;
      const resolved = safePostHogGetFeatureFlag(flagKey, { sendEvent: fireExposure });
      if (!mountedRef.current) return;
      setVariant(resolved);
      if (fireExposure) exposureFiredRef.current = true;
    });

    return () => {
      mountedRef.current = false;
    };
  }, [flagKey, sendEvent]);

  return flagKey ? variant : null;
};

/**
 * Reactive read of a feature flag's JSON payload (config-style flags, no
 * exposure event). Reads on mount and again once PostHog's flags load, so an
 * ops-controlled payload (e.g. a list of offering slugs to hide) takes effect
 * without a deploy. Returns null when unavailable / no payload.
 *
 * @param {string} flagKey
 * @returns {unknown} the flag payload, or null
 */
export const useSafeFeatureFlagPayload = (flagKey) => {
  const [payload, setPayload] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (!flagKey) return undefined;
    mountedRef.current = true;

    if (!isPostHogAvailable()) {
      return () => {
        mountedRef.current = false;
      };
    }

    const read = () => {
      if (!mountedRef.current) return;
      setPayload(safePostHogGetFeatureFlagPayload(flagKey));
    };

    read();
    safePostHogOnFeatureFlags(read);

    return () => {
      mountedRef.current = false;
    };
  }, [flagKey]);

  return flagKey ? payload : null;
};

/**
 * Safe wrapper to get the PostHog instance with null checks.
 *
 * This hook replaces direct usage of usePostHog from posthog-js/react
 * to provide a consistent interface with proper availability checks.
 *
 * @returns {object|null} The PostHog instance or null if unavailable
 */
export const useSafePostHog = () => {
  const [instance, setInstance] = useState(null);

  useEffect(() => {
    if (isPostHogAvailable()) {
      setInstance(posthog);
    }
  }, []);

  return instance;
};

/**
 * Safe wrapper to check if a feature flag is enabled.
 *
 * @param {string} flagKey - The feature flag key to check
 * @returns {boolean} True if the flag is enabled, false otherwise
 */
export const useSafeFeatureEnabled = (flagKey) => {
  const variant = useSafeFeatureFlagVariantKey(flagKey);
  return Boolean(variant);
};

export default useSafeFeatureFlagVariantKey;
